import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Button from '~/modules/components/global/button';
import Modal from '~/modules/components/global/modal';
import useTwoFactor, { STEPS } from '~/modules/hooks/use-two-factor';
import AuthService from '~/modules/services/auth-service';
import PasskeyService from '~/modules/services/passkey-service';
import { REAUTHENTICATION_REQUIRED } from '~/modules/services/two-factor-service';
import {
	AcknowledgeLabel,
	Actions,
	CodeInput,
	CodeRow,
	ErrorText,
	InlineAction,
	ManualEntry,
	MethodAction,
	MethodControls,
	MethodInfo,
	MethodRow,
	QrFrame,
	QrPanel,
	DialogActions,
	RecoveryCodeList,
	RecoveryPanel,
	SecretValue,
	SetupFields,
	SetupStep,
	StateChip,
	Warning
} from '~/modules/components/global/preferences/two-factor-app.css';

/**
 * The authenticator-app section of profile preferences.
 *
 * Separate from the two-factor checkbox above it, which governs the emailed link. The two are
 * different factors and the distinction is worth keeping visible: the emailed link's ceiling is
 * control of the mailbox, and an authenticator is what raises it.
 */
const TwoFactorApp = () => {
	const {
		status, isLoadingStatus, step, enrollment, recoveryCodes, error, setError,
		enroll, isEnrolling, confirm, isConfirming, disable, isDisabling,
		replaceRecoveryCodes, isReplacingCodes, reset, isBusy
	} = useTwoFactor();

	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	// Which credential the person is proving with. A recovery code is offered *instead of* a
	// code rather than after one fails, so a mistyped digit never silently spends one of the ten.
	const [useRecovery, setUseRecovery] = useState(false);
	const [prompt, setPrompt] = useState(null);
	const [acknowledged, setAcknowledged] = useState(false);
	const [copied, setCopied] = useState(false);

	const confirmed = !!status?.confirmed;

	// Hidden when there is nothing to offer *and* nothing to take away.
	//
	// `configured` is false when the factor is switched off or the deployment has no sealing key,
	// and in either case setting one up would fail — so the setup control goes. But somebody who
	// enrolled while it was on still has a credential, and `TwoFactor::disable()` is deliberately
	// left reachable so they can remove it. Hiding the whole section on `configured` alone took
	// away the only control that calls it, leaving a credential the account still reports as
	// confirmed and nobody can get rid of.
	if(isLoadingStatus || (!status?.configured && !confirmed)) {
		return null;
	}

	// Silent copying is not good enough on the one screen where losing the codes is permanent.
	const copyCodes = async() => {
		try {
			// No Clipboard API -- an insecure origin, an old browser -- is the same outcome as a
			// refused one: nothing was copied, so nothing claims to have been. Optional chaining
			// alone resolved to `undefined`, `await` was happy, and the button said "Copied".
			if( typeof navigator.clipboard?.writeText !== 'function' ) {
				throw new Error('Clipboard unavailable');
			}

			await navigator.clipboard.writeText((recoveryCodes || []).join('\n'));
			setCopied(true);
		}
		catch{
			// A refused clipboard is not worth an error banner: the codes are on screen and can
			// be selected. The button simply does not claim to have copied them.
			setCopied(false);
		}
	};

	const clear = () => {
		setPassword('');
		setCode('');
		setUseRecovery(false);
		setPrompt(null);
		setAcknowledged(false);
		setCopied(false);
		reset();
	};

	// What the API will take as proof before it hands out a secret. `passwordRequired` is the
	// older spelling of the same answer, kept as the fallback for a status that predates the
	// three-way one.
	const confirmWith = status?.confirmWith || (status?.passwordRequired ? 'password' : 'none');

	const startSetup = async() => {
		setPrompt('enroll');

		// The password form is rendered; nothing to do until it is submitted.
		if(confirmWith === 'password') {
			return;
		}

		// An account holding a passkey proves it with that one -- the stronger credential, and
		// the one that still works when passwords are switched off. The ceremony leaves the
		// grant the API then accepts in place of a password.
		if(confirmWith === 'passkey') {
			try {
				await PasskeyService.confirm();
			}
			catch(e) {
				// A dismissed prompt is a decision rather than a failure, but the person is still
				// on this step with nothing proved.
				if( e?.name !== 'NotAllowedError' && e?.name !== 'AbortError' ) {
					setError(e?.message || 'That passkey could not be verified');
				}

				return;
			}
		}

		// `none`: an account with nothing to type. A fresh sign-in has left the grant the API
		// wants, or it has aged out and the API says so -- see `renderSetupDialog`.
		await enroll({}).catch(() => { /* surfaced through `error` */ });
	};

	const submitPassword = async(e) => {
		e.preventDefault();
		await enroll({ password }).catch(() => { /* surfaced through `error` */ });
		setPassword('');
	};

	const submitCode = async(e) => {
		e.preventDefault();
		await confirm({ code: code.trim() }).catch(() => { /* surfaced through `error` */ });
		setCode('');
	};

	const proof = () => (useRecovery
		? { recoveryCode: code.trim() }
		: { code: code.trim() });

	const submitDisable = async(e) => {
		e.preventDefault();
		const result = await disable(proof()).catch(() => null);
		if(result) {
			clear();
		}
		setCode('');
	};

	const submitNewCodes = async(e) => {
		e.preventDefault();
		await replaceRecoveryCodes(proof()).catch(() => { /* surfaced through `error` */ });
		setCode('');
	};

	const codeField = (label, autoFocus = true) => (
		<ManualEntry>
			<label htmlFor="px-2fa-code">{label}</label>
			<CodeInput
				id="px-2fa-code"
				data-testid="two-factor-code-input"
				// A one-time code is short-lived and a recovery code is not; neither belongs in
				// a session recording.
				data-private="true"
				type="text"
				// `one-time-code` is what lets iOS and macOS offer the code from Messages or the
				// keychain, and numeric mode brings up the right keypad on a phone.
				autoComplete={useRecovery ? 'off' : 'one-time-code'}
				inputMode={useRecovery ? 'text' : 'numeric'}
				maxLength={useRecovery ? 11 : 6}
				placeholder={useRecovery ? 'recovery code' : '000000'}
				value={code}
				autoFocus={autoFocus}
				onChange={(e) => setCode(e.target.value)}
			/>
		</ManualEntry>
	);

	const recoveryToggle = () => (
		<Button
			variant="ghost-primary"
			type="button"
			onClick={() => {
				setUseRecovery(!useRecovery);
				setCode('');
				setError(null);
			}}
		>
			{useRecovery ? 'Use a code from my app' : 'Use a recovery code'}
		</Button>
	);

	const renderSetupDialog = () => {
		// The secret is on screen: show it as a QR and as text, since a camera is not always
		// available and the code has to be enterable by hand.
		if(step === STEPS.SECRET && enrollment) {
			return (
				<SetupStep>
					<p>Scan this with your authenticator app, then enter the code it shows.</p>
					<QrPanel>
						{/* `sentry-block` / `sentry-mask` and `data-private`: Sentry Replay records
						    error sessions with text and media unmasked (`index.js`) and LogRocket
						    records everything, and everything on this screen is a credential -- the
						    seed as a QR, the seed as text, ten bearer codes. `replay-privacy.js`
						    teaches LogRocket the Sentry classes too; the attribute is belt to that. */}
						<QrFrame data-testid="two-factor-qr" className="sentry-block" data-private="true">
							<QRCodeSVG value={enrollment.uri} size={168} level="M"/>
						</QrFrame>
						{/* Beside the QR rather than beneath it, so the column fills its height
						    instead of leaving a tall gap, and the button lands next to the field
						    it submits. */}
						<SetupFields>
							<ManualEntry>
								<label htmlFor="px-2fa-secret">Or enter this key by hand</label>
								<SecretValue
									id="px-2fa-secret"
									data-testid="two-factor-secret"
									className="sentry-mask"
									data-private="true"
								>
									{enrollment.secret}
								</SecretValue>
							</ManualEntry>
							<form onSubmit={submitCode}>
								<CodeRow>
									{codeField('Code from your app')}
									<Button
										variant="primary"
										type="submit"
										loading={isConfirming}
										disabled={isBusy || code.trim().length < 6}
									>
										Turn on
									</Button>
								</CodeRow>
							</form>
							{error
								? <ErrorText data-testid="two-factor-error">{error}</ErrorText>
								: null}
						</SetupFields>
					</QrPanel>
				</SetupStep>
			);
		}

		// Recovery codes are readable here and nowhere else, so leaving is deliberate.
		if(step === STEPS.CODES && recoveryCodes) {
			return (
				<SetupStep>
					<Warning>
						Save these now — this is the only time they are shown. Each one signs you in
						once if you lose your phone.
					</Warning>
					<RecoveryPanel>
						<RecoveryCodeList
							data-testid="two-factor-recovery-codes"
							className="sentry-mask"
							data-private="true"
						>
							{recoveryCodes.map((recoveryCode) => (
								<li key={recoveryCode}>{recoveryCode}</li>
							))}
						</RecoveryCodeList>
					</RecoveryPanel>
					<AcknowledgeLabel htmlFor="px-2fa-acknowledge">
						<input
							type="checkbox"
							id="px-2fa-acknowledge"
							data-testid="two-factor-acknowledge"
							checked={acknowledged}
							onChange={(e) => setAcknowledged(e.target.checked)}
						/>
						I have saved these codes
					</AcknowledgeLabel>
					<DialogActions>
						<Button
							variant="secondary"
							size="sm"
							type="button"
							data-testid="two-factor-copy"
							onClick={copyCodes}
						>
							{copied ? 'Copied' : 'Copy'}
						</Button>
						<Button
							variant="primary"
							size="sm"
							type="button"
							disabled={!acknowledged}
							onClick={clear}
						>
							Done
						</Button>
					</DialogActions>
				</SetupStep>
			);
		}

		// Nothing left to prove with on this screen: the sign-in that vouched for this person
		// has aged out. A password form here would be a question with no answer for exactly the
		// accounts that land on it -- passkey-only, provider-only, or with the password switched
		// off -- so the way forward is a fresh sign-in.
		if(error === REAUTHENTICATION_REQUIRED) {
			return (
				<SetupStep>
					<p>
						It has been a while since you signed in, so Pixwel needs to see you again
						before an authenticator can be added.
					</p>
					<Actions>
						<Button
							variant="primary"
							type="button"
							data-testid="two-factor-reauth"
							onClick={() => AuthService.logout()}
						>
							Sign in again
						</Button>
					</Actions>
				</SetupStep>
			);
		}

		// Waiting on, or having failed, the proof that is not a password: the passkey prompt or
		// the grant. A retry runs the ceremony again rather than showing a field this account
		// cannot fill in.
		if(confirmWith !== 'password') {
			return (
				<SetupStep>
					<p>
						{confirmWith === 'passkey'
							? 'Confirm with your passkey to add an authenticator app.'
							: 'Starting setup…'}
					</p>
					{error ? <ErrorText data-testid="two-factor-error">{error}</ErrorText> : null}
					{error
						? (
							<Actions>
								<Button
									variant="primary"
									type="button"
									data-testid="two-factor-retry"
									loading={isEnrolling}
									disabled={isBusy}
									onClick={startSetup}
								>
									Try again
								</Button>
							</Actions>
						)
						: null}
				</SetupStep>
			);
		}

		// Asking for the password, before anything has been generated.
		return (
			<form onSubmit={submitPassword}>
				<SetupStep>
					<p>Confirm your password to add an authenticator app.</p>
					<ManualEntry>
						<label htmlFor="px-2fa-password">Password</label>
						<input
							id="px-2fa-password"
							data-testid="two-factor-password-input"
							type="password"
							autoComplete="current-password"
							value={password}
							autoFocus
							onChange={(e) => setPassword(e.target.value)}
						/>
					</ManualEntry>
					<Actions>
						<Button
							variant="primary"
							type="submit"
							loading={isEnrolling}
							disabled={isBusy || !password}
						>
							Continue
						</Button>
					</Actions>
					{error ? <ErrorText data-testid="two-factor-error">{error}</ErrorText> : null}
				</SetupStep>
			</form>
		);
	};

	const renderProofDialog = ({ title, submit, action, loading, variant = 'primary' }) => (
		<form onSubmit={submit}>
			<SetupStep>
				<p>{title}</p>
				<CodeRow>
					{codeField(useRecovery ? 'Recovery code' : 'Code from your app')}
					<Button
						variant={variant}
						type="submit"
						loading={loading}
						disabled={isBusy || !code.trim()}
					>
						{action}
					</Button>
					{recoveryToggle()}
				</CodeRow>
				{error ? <ErrorText data-testid="two-factor-error">{error}</ErrorText> : null}
			</SetupStep>
		</form>
	);

	const dialogTitle = () => {
		if(prompt === 'disable') {
			return 'Turn off authenticator app';
		}
		if(prompt === 'recovery') {
			return 'New recovery codes';
		}
		return step === STEPS.CODES ? 'Recovery codes' : 'Authenticator app';
	};

	const dialogBody = () => {
		if(prompt === 'disable') {
			return renderProofDialog({
				// What sign-in falls back to depends on things this dialog cannot see -- whether
				// the emailed link is on or enforced, whether a passkey exists, whether a password
				// is still accepted at all -- so it promises none of them by name.
				title: 'Enter a code to confirm. Sign-in then uses whatever else this account has '
					+ '-- the emailed link, a passkey, or a password where one is still accepted.',
				submit: submitDisable,
				// Distinct from the "Turn off" that opened this dialog, so the destructive
				// confirmation cannot be mistaken for the thing that asked for it.
				action: 'Turn off authenticator',
				loading: isDisabling,
				variant: 'danger'
			});
		}
		if(prompt === 'recovery' && step !== STEPS.CODES) {
			return renderProofDialog({
				title: 'Enter a code to receive a new set. Your existing codes stop working.',
				submit: submitNewCodes,
				action: 'Generate',
				loading: isReplacingCodes
			});
		}
		return renderSetupDialog();
	};

	// Closing mid-flow is allowed everywhere except on the recovery codes, which cannot be
	// retrieved again once this closes.
	const closable = step !== STEPS.CODES;

	return (
		<>
			<MethodRow>
				<MethodInfo>
					<strong>Authenticator app</strong>
					<span>
						{confirmed
							? (
								<>
									{`${status?.recoveryCodes ?? 0} recovery codes left`}
									{/*
										Reissuing is putting a credential *up*, so the API gates it
										behind the same policy as setup and answers 404 when the
										factor is off. The section itself stays visible in that
										state so the credential can still be removed — offering
										this alongside would be offering a button that fails.
									*/}
									{ status?.configured
										? (
											<>
												{' · '}
												<InlineAction
													type="button"
													onClick={() => {
														clear();
														setPrompt('recovery');
													}}
												>
													New codes
												</InlineAction>
											</>
										)
										: null }
								</>
							)
							: 'Codes from an app like 1Password or Google Authenticator'}
					</span>
				</MethodInfo>
				<MethodControls>
					<StateChip on={confirmed} data-testid="two-factor-app-state">
						{confirmed ? 'On' : 'Off'}
					</StateChip>
					<MethodAction>
						{confirmed
							? (
								<Button
									variant="secondary"
									size="sm"
									type="button"
									data-testid="two-factor-turn-off"
									onClick={() => {
										clear();
										setPrompt('disable');
									}}
								>
									Turn off
								</Button>
							)
							: (
								<Button
									variant="primary"
									size="sm"
									type="button"
									data-testid="two-factor-set-up"
									onClick={() => {
										clear();
										startSetup();
									}}
								>
									Set up
								</Button>
							)}
					</MethodAction>
				</MethodControls>
			</MethodRow>

			<Modal
				isOpen={!!prompt}
				size={560}
				shouldCloseOnOverlayClick={closable}
				onRequestClose={closable ? clear : undefined}
			>
				<Modal.Header>
					<h3>{dialogTitle()}</h3>
				</Modal.Header>
				<Modal.Body>
					{dialogBody()}
				</Modal.Body>
			</Modal>
		</>
	);
};

export default TwoFactorApp;
